import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToastActions } from 'stores/toastStore';

import type { LifecycleConfig, ShowFn, ShowResult, UseAdminFormConfig } from './types';
import type { FormControlsWithDataSource } from './useFormControlsFor';
import useFormControlsFor from './useFormControlsFor';
import useSingle from './useSingle';

export interface AdminFormControls<TData, TForm extends object> extends FormControlsWithDataSource<TForm> {
  /** The raw API show result (before form transformation). */
  data: TData | undefined;
  /** Navigate to the onSuccess path (for Cancel buttons). */
  goBack: () => void;
  /** Submit the form, then reset the given fields instead of navigating. */
  submitAndReset: (fields: (keyof TForm & string)[]) => Promise<void>;
}

export default function useAdminForm<I extends string, F extends ShowFn<I>, TForm extends object, TPayload>(
  config: UseAdminFormConfig<I, F, TForm, TPayload>
): AdminFormControls<ShowResult<F>, TForm> {
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const createAnotherFieldsRef = useRef<(keyof TForm & string)[] | null>(null);

  const lifecycle = useCallback(
    (lc: LifecycleConfig | undefined) => {
      if (lc?.toast) addToast({ text: lc.toast });
      if (lc?.navigate) navigate(lc.navigate);
    },
    [addToast, navigate]
  );

  const request = useSingle({
    id: config.id,
    show: config.show,
    include: config.include,
    queryKey: config.queryKey,
    create: async (payload: any) => {
      try {
        const result = await config.create(payload);
        const resetFields = createAnotherFieldsRef.current;
        createAnotherFieldsRef.current = null;

        if (resetFields) {
          const createLc = config.onCreateSuccess ?? config.onSuccess;
          if (createLc?.toast) addToast({ text: createLc.toast });
          // Reset specified fields via setValue (set below after form is created)
          resetFieldsFn.current?.(resetFields);
        } else {
          lifecycle(config.onCreateSuccess ?? config.onSuccess);
        }
        return result;
      } catch (err) {
        lifecycle(config.onError);
        throw err;
      }
    },
    update: async (id: string, payload: any) => {
      try {
        const result = await config.update(id, payload);
        lifecycle(config.onUpdateSuccess ?? config.onSuccess);
        return result;
      } catch (err) {
        lifecycle(config.onError);
        throw err;
      }
    },
  });

  const formControls = useFormControlsFor(request, {
    schema: config.schema,
    validate: config.validate,
    toFormValues: config.toFormValues,
    toPayload: config.toPayload,
  });

  // Wire up field reset for "create another" flow
  const resetFieldsFn = useRef<((fields: (keyof TForm & string)[]) => void) | null>(null);
  resetFieldsFn.current = (fields) => {
    for (const field of fields) {
      formControls.setValue(field, '', { validate: false });
    }
  };

  const submitAndReset = useCallback(
    async (fields: (keyof TForm & string)[]) => {
      createAnotherFieldsRef.current = fields;
      await formControls.submit();
    },
    [formControls]
  );

  const goBack = useCallback(() => {
    const path = config.onSuccess?.navigate ?? config.onUpdateSuccess?.navigate ?? config.onCreateSuccess?.navigate;
    if (path) navigate(path);
  }, [config.onSuccess?.navigate, config.onUpdateSuccess?.navigate, config.onCreateSuccess?.navigate, navigate]);

  return {
    ...formControls,
    data: request.value,
    goBack,
    submitAndReset,
  };
}
